'use client';

import * as React from 'react';
import { FileUploadArea, Button } from '@/components/ui';
import { FileUp, BarChart, Download } from 'lucide-react';

interface AppEmptyStateProps {
  /**
   * Callback function when files are uploaded
   */
  onFilesSelected: (files: FileList | null) => void;
  
  /**
   * Optional engine status information to show loading progress
   */
  engineStatus?: {
    isEngineReady: boolean;
    fileLoadingStatus: 'idle' | 'loading' | 'success' | 'error';
    hasFileId: boolean;
    hasSampleId: boolean;
  };
}

/**
 * Empty state component shown when no datasets are available
 * Displays a welcome message and file upload area
 */
export function AppEmptyState({ onFilesSelected, engineStatus }: AppEmptyStateProps) {
  // Determine the status message based on engine and file loading state
  const getStatusMessage = () => {
    if (!engineStatus) {
      return {
        title: 'Analyze Your Data',
        subtitle: 'Upload files to explore, visualize, and gain insights',
        isLoading: false
      };
    }

    const { isEngineReady, fileLoadingStatus, hasFileId, hasSampleId } = engineStatus;

    // Engine is still initializing
    if (!isEngineReady) {
      return {
        title: 'Preparing Data Engine...',
        subtitle: 'Setting up the analysis environment in the background',
        isLoading: true
      };
    }

    // Engine is ready, but we're loading a file/sample
    if ((hasFileId || hasSampleId) && fileLoadingStatus === 'loading') {
      return {
        title: 'Loading Your Data...',
        subtitle: hasSampleId ? 'Preparing sample dataset' : 'Loading your file',
        isLoading: true
      };
    }

    // Engine is ready, file loading failed
    if ((hasFileId || hasSampleId) && fileLoadingStatus === 'error') {
      return {
        title: 'Error Loading Data',
        subtitle: 'There was a problem loading your data. You can try uploading a new file.',
        isLoading: false
      };
    }

    // Default: Engine ready, ready for file upload
    return {
      title: 'Analyze Your Data',
      subtitle: 'Upload files to explore, visualize, and gain insights',
      isLoading: false
    };
  };

  const status = getStatusMessage();
  const canUploadFiles = !engineStatus || (engineStatus.isEngineReady && engineStatus.fileLoadingStatus !== 'loading');

  return (
    <div className="flex flex-col items-center w-full h-full pt-16">
      {/* Title at the top of the page */}
      <div className="text-center mb-12">
        <div className="flex items-center justify-center gap-2 mb-2">
          <h1 className="text-4xl font-bold">{status.title}</h1>
          {status.isLoading && (
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          )}
        </div>
        <p className="text-lg text-muted-foreground">
          {status.subtitle}
        </p>
      </div>

      {/* Main content with centered layout */}
      <div className="flex flex-col items-center w-full max-w-4xl mx-auto px-4">
        {/* Upload area */}
        <div className="w-full mb-12">
          <FileUploadArea
            formatName={['CSV', 'JSON', 'Parquet', 'Arrow', 'TSV']}
            multiple={true}
            onFilesSelected={canUploadFiles ? onFilesSelected : () => {}}
            className={`w-full h-64 bg-background/50 border-dashed ${!canUploadFiles ? 'opacity-50 pointer-events-none' : ''}`}
            buttonText={canUploadFiles ? "Upload Data Files" : "Preparing engine..."}
            icon={<FileUp className="w-8 h-8 text-primary" />}
          />
          {!canUploadFiles && (
            <p className="text-center text-sm text-muted-foreground mt-2">
              File upload will be available once the engine is ready
            </p>
          )}
        </div>

        {/* Features section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full">
          <div className="flex flex-col items-center text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-background mb-4 shadow-sm">
              <FileUp className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-lg font-medium mb-2">Easy Data Import</h3>
            <p className="text-sm text-muted-foreground">
              Upload CSV, JSON, Parquet, Arrow, or TSV files to analyze your data
            </p>
          </div>
          
          <div className="flex flex-col items-center text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-background mb-4 shadow-sm">
              <BarChart className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-lg font-medium mb-2">Powerful Analysis</h3>
            <p className="text-sm text-muted-foreground">
              Filter, sort, and transform your data with an intuitive interface
            </p>
          </div>
          
          <div className="flex flex-col items-center text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-background mb-4 shadow-sm">
              <Download className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-lg font-medium mb-2">Export Results</h3>
            <p className="text-sm text-muted-foreground">
              Export your analysis results in various formats for sharing
            </p>
          </div>
        </div>
      </div>
    </div>
  );
} 